import React, { useEffect, useState } from "react";
import { Search, Trash2, CheckCircle } from "lucide-react";
// @ts-ignore
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import axios from "axios";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import LogButton from "../common/LogButton";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import { styles } from "../../styles/pages/JobTitle/JobTitleTableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

type StatusType = "Active" | "Inactive";

interface JobTitle {
  id: number;
  job_title: string;
  status: StatusType;
  log: string;
}

const JobTitleTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobtitles, setJobTitles] = useState<JobTitle[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof JobTitle | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedJobTitleId, setSelectedJobTitleId] = useState<number | null>(
    null
  );
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const token = sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchJobTitles = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) throw new Error("Unauthorized: Please log in.");
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/jobtitle/search/list`,
          { search: "" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === 200) {
          const formattedJobTitles: JobTitle[] = response.data.data
            .map((jobtitle: any) => ({
              id: jobtitle.id,
              job_title: jobtitle.job_title,
              status: jobtitle.status === 1 ? "Active" : "Inactive",
              log: jobtitle.log,
            }))
            .sort((a: JobTitle, b: JobTitle) => b.id - a.id);
          setJobTitles(formattedJobTitles);
        } else {
          setJobTitles([]);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setJobTitles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobTitles();
  }, [token]);

  const toggleStatus = async (id: number) => {
    try {
      if (!token) throw new Error("Authentication error! Please log in.");
      const jobtitle = jobtitles.find((s: JobTitle) => s.id === id);
      if (!jobtitle) return;
      const newStatus = jobtitle.status === "Active" ? 0 : 1;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/jobtitle`,
        { job_title_id: id, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setJobTitles((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, status: newStatus === 1 ? "Active" : "Inactive" }
              : s
          )
        );
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteClick = (jobtitleId: number) => {
    setSelectedJobTitleId(jobtitleId);
    openModal();
  };

  const handleConfirmDelete = async () => {
    if (!selectedJobTitleId || !token) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/jobtitle/delete`,
        { job_title_id: selectedJobTitleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === 200) {
        setJobTitles((prev) => {
          const updated = prev.filter((s) => s.id !== selectedJobTitleId);
          if (updated.length > 0 && displayedJobTitles.length === 1) {
            setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
          }
          return updated;
        });
        closeModal();
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to delete job title.");
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to delete job title."
      );
      setErrorModalOpen(true);
    } finally {
      closeModal();
    }
  };

  const handleSort = (column: keyof JobTitle) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredJobTitles = jobtitles.filter((jobtitle) =>
    jobtitle.job_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedJobTitles = [...filteredJobTitles].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortOrder === "asc"
      ? (valA as number) - (valB as number)
      : (valB as number) - (valA as number);
  });

  const displayedJobTitles = sortedJobTitles.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredJobTitles.length / rowsPerPage);

  const handleDownload = () => {
    const data = searchTerm
      ? filteredJobTitles
      : sortColumn
      ? sortedJobTitles
      : jobtitles;

    if (!data.length) return;

    const formattedData = data.map((jobtitle: JobTitle) => ({
      "Job Title": jobtitle.job_title,
      Status: jobtitle.status,
      Log: jobtitle.log,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Job Title List"]], { origin: "A1" });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 }, // Start A1
        e: { r: 0, c: headers.length - 1 }, // End across number of columns
      },
    ];

    // 3. Style title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Style each header cell
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) => String(row[key as keyof typeof row])?.length || 0
          ),
          "Job Title List".length
        ) + 2,
    }));

    // 8. Export
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "JobTitles");
    XLSX.writeFile(
      workbook,
      searchTerm ? `JobTitleData_${searchTerm}.xlsx` : "JobTitleData.xlsx"
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Job Title List</h3>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(val) => {
              setRowsPerPage(val);
              setCurrentPage(1);
            }}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/job-title-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Job Title
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.th}
                  onClick={() => handleSort("job_title")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Job Title</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Status</p>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.th}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className={styles.loadingText}>
                    <Loader />
                  </td>
                </tr>
              ) : displayedJobTitles.length ? (
                displayedJobTitles.map((jobtitle) => (
                  <tr key={jobtitle.id} className={styles.tbodyRow}>
                    <td className={styles.td}>{jobtitle.job_title}</td>
                    <td className={styles.td}>
                      <div className={styles.statusContainer}>
                        <button
                          onClick={() => toggleStatus(jobtitle.id)}
                          className={`${styles.statusToggle} ${
                            jobtitle.status === "Active"
                              ? styles.statusToggleActive
                              : styles.statusToggleInactive
                          }`}
                        >
                          <div
                            className={`${styles.statusToggleCircle} ${
                              jobtitle.status === "Active"
                                ? styles.statusToggleCircleActive
                                : styles.statusToggleCircleInactive
                            }`}
                          ></div>
                        </button>
                        <span
                          className={`${styles.statusBadge} ${
                            jobtitle.status === "Active"
                              ? styles.statusActive
                              : styles.statusInactive
                          }`}
                        >
                          {jobtitle.status}
                        </span>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                        <Link
                          to={`/job-title-edit/${jobtitle.id}`}
                          className="inline-flex"
                        >
                          <button className={styles.editButton}>
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="text-white text-sm"
                            />
                          </button>
                        </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteClick(jobtitle.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-white text-sm"
                          />
                        </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.td}>
                      <LogButton log={jobtitle.log} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className={styles.noDataText}>
                    No records found...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredJobTitles.length}
          handlePageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <div className={styles.modalIconContainer}>
          <Trash2 className={styles.modalIcon} />
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this job title?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className={styles.modalDeleteButton}
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Job Title Status Update Successfully!
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Error Modal for Delete Failure */}
      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-red-100 dark:bg-red-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-red-200 text-red-600 rounded-full">
            <Trash2 className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Delete Failed!
        </h4>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{errorMessage}</p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setErrorModalOpen(false)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default JobTitleTable;
